import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Brain, FileText, MessageSquare, ArrowRight, AlertTriangle, CheckCircle, XCircle, Settings, Thermometer, Shuffle } from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

const contextExamples = [
  { label: 'GPT-4', tokens: '128K', color: 'from-green-500 to-emerald-500' },
  { label: 'Claude', tokens: '200K', color: 'from-electric-purple to-electric-magenta' },
  { label: 'Gemini', tokens: '1M+', color: 'from-blue-500 to-cyan-500' },
]

const functionParams = [
  { name: 'context', desc: 'History, files, code', icon: FileText, color: 'text-electric-purple' },
  { name: 'prompt', desc: 'Your question/task', icon: MessageSquare, color: 'text-electric-cyan' },
  { name: 'system', desc: 'Behavior instructions', icon: Settings, color: 'text-electric-orange' },
  { name: '...', desc: 'temp, top_p, max_tokens', icon: Thermometer, color: 'text-electric-magenta' },
]

export default function LLMExplainerSlide({ isActive }: SlideProps) {
  const [contextFill, setContextFill] = useState(0)
  const [isAnimating, setIsAnimating] = useState(true)

  // Animate context fill
  useEffect(() => {
    if (!isActive || !isAnimating) return

    const interval = setInterval(() => {
      setContextFill((prev) => {
        if (prev >= 100) return 0
        return prev + 1
      })
    }, 80)

    return () => clearInterval(interval)
  }, [isActive, isAnimating])

  const getContextStatus = () => {
    if (contextFill < 50) return { color: 'text-green-400', bg: 'bg-green-500', label: 'Optimal', icon: CheckCircle }
    if (contextFill < 80) return { color: 'text-yellow-400', bg: 'bg-yellow-500', label: 'Warning', icon: AlertTriangle }
    return { color: 'text-red-400', bg: 'bg-red-500', label: 'Degraded', icon: XCircle }
  }

  const status = getContextStatus()
  const StatusIcon = status.icon

  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col">
        {/* Header */}
        <StaggerItem>
          <div className="mb-8 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
              Understanding <span className="text-gradient">LLMs</span>
            </h2>
            <p className="text-lg text-white/60">
              A non-deterministic function with key parameters
            </p>
          </div>
        </StaggerItem>

        {/* Main content - two columns */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Left: Function Diagram */}
          <StaggerItem>
            <div className="glass rounded-2xl p-4 h-full">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5 text-electric-cyan" />
                The LLM Function
              </h3>

              {/* Function visualization */}
              <div className="flex flex-col items-center gap-3">
                {/* Input parameters grid */}
                <div className="grid grid-cols-2 gap-3 w-full">
                  {functionParams.map((param, index) => {
                    const Icon = param.icon
                    return (
                      <motion.div
                        key={param.name}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + index * 0.1 }}
                        className="glass rounded-xl p-3"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`w-5 h-5 ${param.color}`} />
                          <span className="text-sm font-mono text-white">{param.name}</span>
                        </div>
                        <p className="text-xs text-white/50">{param.desc}</p>
                      </motion.div>
                    )
                  })}
                </div>

                {/* Arrow down */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <ArrowRight className="w-6 h-6 text-white/40 rotate-90" />
                </motion.div>

                {/* LLM Box */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 }}
                  className="relative w-full"
                >
                  <div className="bg-gradient-to-br from-electric-cyan/20 to-electric-purple/20 rounded-xl p-4 border border-electric-cyan/30">
                    <div className="flex items-center gap-3 justify-center">
                      <Brain className="w-8 h-8 text-electric-cyan" />
                      <div>
                        <code className="text-lg font-bold text-white">f(ctx, prompt, sys, ...)</code>
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-electric-cyan/10 rounded-xl blur-xl -z-10" />
                </motion.div>

                {/* Arrow down */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <ArrowRight className="w-6 h-6 text-white/40 rotate-90" />
                </motion.div>

                {/* Output with non-deterministic note */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                  className="glass rounded-xl p-3 w-full"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Shuffle className="w-5 h-5 text-electric-orange" />
                    <span className="text-base font-semibold text-gradient">Response</span>
                  </div>
                  <p className="text-sm text-white/50 text-center">Non-deterministic output (same input ≠ same output)</p>
                </motion.div>
              </div>

              {/* Model context sizes */}
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-xs text-white/40 mb-1.5">Context Window Sizes:</p>
                <div className="flex gap-2 flex-wrap">
                  {contextExamples.map((model) => (
                    <div
                      key={model.label}
                      className="glass rounded-full px-3 py-1 text-xs"
                    >
                      <span className="text-white/70">{model.label}:</span>{' '}
                      <span className={`font-mono bg-gradient-to-r ${model.color} bg-clip-text text-transparent`}>
                        {model.tokens}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </StaggerItem>

          {/* Right: Context Window Gauge */}
          <StaggerItem>
            <div className="glass rounded-2xl p-4 h-full">
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-electric-orange" />
                Context Window Limits
              </h3>

              {/* Gauge visualization */}
              <div className="flex flex-col items-center">
                {/* Vertical gauge */}
                <div
                  className="relative w-14 h-40 rounded-full bg-space-700 overflow-hidden cursor-pointer"
                  onClick={() => setIsAnimating(!isAnimating)}
                >
                  {/* Fill */}
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 transition-all duration-100"
                    style={{
                      height: `${contextFill}%`,
                      background: contextFill < 50
                        ? 'linear-gradient(to top, #22c55e, #4ade80)'
                        : contextFill < 80
                          ? 'linear-gradient(to top, #eab308, #facc15)'
                          : 'linear-gradient(to top, #ef4444, #f87171)',
                    }}
                  />

                  {/* Zone markers */}
                  <div className="absolute inset-0 flex flex-col justify-between py-2 pointer-events-none">
                    <div className="border-b border-red-500/50 mx-2" />
                    <div className="border-b border-yellow-500/50 mx-2" />
                    <div />
                  </div>

                  {/* Zone labels */}
                  <div className="absolute right-full mr-2 inset-y-0 flex flex-col justify-between text-xs py-2">
                    <span className="text-red-400">100%</span>
                    <span className="text-yellow-400">80%</span>
                    <span className="text-green-400">50%</span>
                    <span className="text-white/40">0%</span>
                  </div>
                </div>

                {/* Current status */}
                <motion.div
                  key={status.label}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`mt-3 flex items-center gap-2 ${status.color}`}
                >
                  <StatusIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">{status.label}</span>
                  <span className="text-xs text-white/50">({contextFill}%)</span>
                </motion.div>

                <p className="text-[10px] text-white/40 mt-1">
                  {isAnimating ? 'Click to pause' : 'Click to animate'}
                </p>
              </div>

              {/* Warning cards */}
              <div className="mt-4 space-y-3">
                <div className={`rounded-xl p-3 transition-all duration-300 ${contextFill >= 80 ? 'bg-red-500/20 border border-red-500/30' : 'bg-white/5'}`}>
                  <div className="flex items-start gap-3">
                    <XCircle className={`w-6 h-6 mt-0.5 flex-shrink-0 ${contextFill >= 80 ? 'text-red-400' : 'text-white/30'}`} />
                    <div>
                      <p className={`text-base font-semibold ${contextFill >= 80 ? 'text-red-400' : 'text-white/50'}`}>
                        Near Limit (80-100%)
                      </p>
                      <p className="text-sm text-white/40">
                        Attention becomes diluted
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`rounded-xl p-3 transition-all duration-300 ${contextFill >= 50 && contextFill < 80 ? 'bg-yellow-500/20 border border-yellow-500/30' : 'bg-white/5'}`}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className={`w-6 h-6 mt-0.5 flex-shrink-0 ${contextFill >= 50 && contextFill < 80 ? 'text-yellow-400' : 'text-white/30'}`} />
                    <div>
                      <p className={`text-base font-semibold ${contextFill >= 50 && contextFill < 80 ? 'text-yellow-400' : 'text-white/50'}`}>
                        Warning Zone (50-80%)
                      </p>
                      <p className="text-sm text-white/40">
                        "Lost in the middle" effect
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`rounded-xl p-3 transition-all duration-300 ${contextFill < 50 ? 'bg-green-500/20 border border-green-500/30' : 'bg-white/5'}`}>
                  <div className="flex items-start gap-3">
                    <CheckCircle className={`w-6 h-6 mt-0.5 flex-shrink-0 ${contextFill < 50 ? 'text-green-400' : 'text-white/30'}`} />
                    <div>
                      <p className={`text-base font-semibold ${contextFill < 50 ? 'text-green-400' : 'text-white/50'}`}>
                        Optimal (0-50%)
                      </p>
                      <p className="text-sm text-white/40">
                        Best performance, full attention
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </StaggerItem>
        </div>

        {/* Bottom insight */}
        <StaggerItem>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="mt-3 mb-12 text-center"
          >
            <p className="text-sm text-white/50">
              <span className="text-electric-cyan">Pro tip:</span> Keep context focused and relevant for best results
            </p>
          </motion.div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
